// @ts-nocheck
import { Building2, Phone, Key, Save, AlertCircle, CheckCircle2, Copy, ExternalLink, Plus, Trash2, Star, Clock, MessageSquare, UserSquare, Tag, Volume2, Settings, ListTree, MessageCircle, Smartphone, Bot, Ban, ShieldAlert } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useApi } from '../hooks/useApi'
import { formatPhoneNumberForDisplay, formatPhoneNumberForSave, formatPhoneNumberAsYouType } from '../utils/phoneNumberHelper'
import BusinessHoursSettings from '../components/BusinessHours/BusinessHoursSettings'
import TaskRoutingSettings from '../components/TaskRoutingSettings'
import MobileRoutingSettings from '../components/MobileRoutingSettings'
import PhoneNumberOptionsSettings from '../components/PhoneNumberOptionsSettings'
import WelcomeMessageSettings from '../components/WelcomeMessageSettings'
import WebCallLinksSettings from '../components/WebCallLinksSettings'
import IvrMenusSettings from '../components/IvrMenusSettings'
import IvrPhoneNumberSettings from '../components/IvrPhoneNumberSettings'
import LineSettingsPage from './LineSettingsPage'
import BlockListPage from './BlockListPage'
import CoverageAuditView from '../components/CoverageAuditView'

// タブ定義: adminOnly のタブは管理者のみ表示・アクセス可能
const SETTINGS_TABS = [
  { id: 'organization', label: '組織情報', icon: Building2, adminOnly: true },
  { id: 'phones', label: '電話番号', icon: Phone, adminOnly: false },
  { id: 'ivr', label: 'IVR', icon: ListTree, adminOnly: true },
  { id: 'web-call-links', label: 'Web通話リンク', icon: ExternalLink, adminOnly: true },
  { id: 'line', label: 'LINE連携', icon: MessageCircle, adminOnly: true },
  { id: 'line-ops', label: 'LINE応対', icon: Bot, adminOnly: false },
  { id: 'blocklist', label: '着信拒否', icon: Ban, adminOnly: false },
  { id: 'audit', label: '監査', icon: ShieldAlert, adminOnly: true },
  { id: 'other', label: 'その他', icon: Settings, adminOnly: true },
]

export default function SettingsPage() {
  const { tab: activeTab } = useParams()
  const { user, isAdmin } = useAuthStore((state) => ({
    user: state.user,
    isAdmin: state.isAdmin(),
  }))
  // 権限に応じて表示可能なタブを解決する（URL不正時はデフォルトタブへリダイレクト）
  const visibleTabs = SETTINGS_TABS.filter((t) => isAdmin || !t.adminOnly)
  const isValidTab = visibleTabs.some((t) => t.id === activeTab)
  const api = useApi()
  const navigate = useNavigate()
  const [hasChanges, setHasChanges] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 組織設定
  const [orgSettings, setOrgSettings] = useState({
    organizationId: '',
    name: '',
    phoneNumber: '',
    email: '',
    address: '',
    slackWebhookUrl: '',
    customerTags: [], // 顧客タグのデフォルト選択肢
  })

  // Twilio設定
  const [twilioSettings, setTwilioSettings] = useState({
    accountSid: '',
    authToken: '',
    apiKey: '',
    apiSecret: '',
    twimlAppSid: '',
  })

  // LINE設定
  const [lineSettings, setLineSettings] = useState({
    channelId: '',
    channelAccessToken: '',
    channelSecret: '',
    webhookToken: '',
    friendUrl: '',
  })

  // 電話番号リスト
  const [phoneNumbers, setPhoneNumbers] = useState([])
  const [businessStatuses, setBusinessStatuses] = useState({})
  const [showAddPhoneForm, setShowAddPhoneForm] = useState(false)
  const [newPhone, setNewPhone] = useState({
    phoneNumber: '',
    name: '',
  })

  // 営業時間設定モーダル
  const [selectedPhoneForBusinessHours, setSelectedPhoneForBusinessHours] = useState(null)

  // タスクルーティング設定モーダル
  const [selectedPhoneForTaskRouting, setSelectedPhoneForTaskRouting] = useState(null)
  const [selectedPhoneForMobileRouting, setSelectedPhoneForMobileRouting] = useState(null)

  // タグ・通話種別設定モーダル
  const [selectedPhoneForOptions, setSelectedPhoneForOptions] = useState(null)

  // 着信メッセージ設定モーダル
  const [selectedPhoneForWelcomeMessage, setSelectedPhoneForWelcomeMessage] = useState(null)

  // IVR設定モーダル
  const [selectedPhoneForIvr, setSelectedPhoneForIvr] = useState(null)

  // Webhook URL（動的生成）
  const webhookUrl = `${import.meta.env.VITE_TWILIO_WEBHOOK_URL || 'https://telephone.dance'}/twilio/voice/incoming`

  // 組織情報と電話番号リストを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user?.organizationId) {
          navigate('/login', { replace: true })
          return
        }
        setLoading(true)
        setError(null)
        
        // 組織情報取得（管理者のみ）
        if (isAdmin) {
          const data = await api.get('/api/organization')
          setOrgSettings({
            organizationId: data.organizationId || '',
            name: data.name || '',
            phoneNumber: formatPhoneNumberForDisplay(data.phoneNumber) || '',
            email: data.email || '',
            address: data.address || '',
            slackWebhookUrl: data.slackWebhookUrl || '',
            customerTags: data.customerTags || [],
          })
          setTwilioSettings({
            accountSid: data.twilioAccountSid || '',
            authToken: '', // セキュリティのため表示しない
            apiKey: data.twilioApiKey || '',
            apiSecret: '', // セキュリティのため表示しない
            twimlAppSid: data.twilioTwimlAppSid || '',
          })
          setLineSettings({
            channelId: data.lineChannelId || '',
            channelAccessToken: '', // セキュリティのため表示しない
            channelSecret: '', // セキュリティのため表示しない
            webhookToken: data.lineWebhookToken || '',
            friendUrl: data.lineFriendUrl || '',
          })
        }

        // 電話番号リスト取得
        const phoneData = await api.get('/api/phone-numbers')
        setPhoneNumbers(phoneData.items || [])
      } catch (err) {
        console.error('データ取得エラー:', err)
        setError('データの取得に失敗しました')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [navigate, user?.organizationId, isAdmin, api])

  // 電話番号ごとの営業時間ステータスを取得
  // 営業時間ステータスを取得する関数（再利用可能）
  const fetchStatuses = async () => {
    if (phoneNumbers.length === 0) return
    const entries = await Promise.all(
      phoneNumbers.map(async (phone) => {
        try {
          const data = await api.get(`/api/phone-numbers/${phone.phoneNumberId}/business-hours/status`)
          return [phone.phoneNumberId, data]
        } catch (err) {
          console.warn('営業時間ステータス取得失敗:', err)
          return [phone.phoneNumberId, null]
        }
      })
    )
    setBusinessStatuses(Object.fromEntries(entries))
  }

  useEffect(() => {
    fetchStatuses()
  }, [phoneNumbers, api])

  // 組織設定を保存
  const handleSaveOrg = async () => {
    try {
      setLoading(true)
      const updatedOrg = await api.put('/api/organization', {
        name: orgSettings.name,
        phoneNumber: formatPhoneNumberForSave(orgSettings.phoneNumber) || orgSettings.phoneNumber,
        email: orgSettings.email,
        address: orgSettings.address,
        slackWebhookUrl: orgSettings.slackWebhookUrl || undefined,
        customerTags: orgSettings.customerTags,
        twilioAccountSid: twilioSettings.accountSid,
        twilioAuthToken: twilioSettings.authToken || undefined,
        twilioApiKey: twilioSettings.apiKey || undefined,
        twilioApiSecret: twilioSettings.apiSecret || undefined,
        twilioTwimlAppSid: twilioSettings.twimlAppSid || undefined,
        lineChannelId: lineSettings.channelId || undefined,
        lineChannelAccessToken: lineSettings.channelAccessToken || undefined,
        lineChannelSecret: lineSettings.channelSecret || undefined,
        lineFriendUrl: lineSettings.friendUrl || undefined,
      })

      // LINE webhookToken が新規生成された場合に反映
      if (updatedOrg?.lineWebhookToken) {
        setLineSettings(prev => ({ ...prev, webhookToken: updatedOrg.lineWebhookToken }))
      }

      setSaved(true)
      setHasChanges(false)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('保存エラー:', err)
      alert(`エラー: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  // 電話番号を追加
  const handleAddPhone = async () => {
    try {
      if (!newPhone.phoneNumber || !newPhone.name) {
        alert('電話番号と名称を入力してください')
        return
      }

      setLoading(true)
      const phoneNumberToSave = formatPhoneNumberForSave(newPhone.phoneNumber)
      if (!phoneNumberToSave) {
        alert('有効な電話番号を入力してください')
        setLoading(false)
        return
      }
      
      const data = await api.post('/api/phone-numbers', {
        ...newPhone,
        phoneNumber: phoneNumberToSave,
      })

      setPhoneNumbers([...phoneNumbers, data])
      setNewPhone({
        phoneNumber: '',
        name: '',
      })
      setShowAddPhoneForm(false)
      alert('電話番号を追加しました')
    } catch (err) {
      console.error('追加エラー:', err)
      alert(`エラー: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  // 電話番号を削除
  const handleDeletePhone = async (phoneNumberId) => {
    if (!confirm('この電話番号を削除しますか？')) {
      return
    }

    try {
      setLoading(true)
      await api.delete(`/api/phone-numbers/${phoneNumberId}`)

      setPhoneNumbers(phoneNumbers.filter(p => p.phoneNumberId !== phoneNumberId))
      alert('電話番号を削除しました')
    } catch (err) {
      console.error('削除エラー:', err)
      alert(`エラー: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Webhook URLをコピー
  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl)
    alert('Webhook URLをコピーしました')
  }

  // タブ未指定・不正・権限外の場合はデフォルトタブへ
  if (!isValidTab) {
    return <Navigate to={`/settings/${visibleTabs[0].id}`} replace />
  }

  if (loading && !orgSettings.organizationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* エラーメッセージ */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* 保存成功メッセージ */}
      {saved && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800 dark:text-green-300">設定を保存しました</p>
        </div>
      )}

      {/* タブナビゲーション */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/settings/${t.id}`)}
              className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                activeTab === t.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 組織情報タブ */}
      {isAdmin && activeTab === 'organization' && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">組織情報</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">組織の基本情報を設定します</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                組織名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={orgSettings.name}
                onChange={(e) => {
                  setOrgSettings({ ...orgSettings, name: e.target.value })
                  setHasChanges(true)
                }}
                className="input-field"
                placeholder="例: NPO法人サンプル"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                代表電話番号
              </label>
              <input
                type="tel"
                value={orgSettings.phoneNumber}
                onChange={(e) => {
                  const formatted = formatPhoneNumberAsYouType(e.target.value)
                  setOrgSettings({ ...orgSettings, phoneNumber: formatted })
                  setHasChanges(true)
                }}
                className="input-field"
                placeholder="03-1234-5678"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">メールアドレス</label>
            <input
              type="email"
              value={orgSettings.email}
              onChange={(e) => {
                setOrgSettings({ ...orgSettings, email: e.target.value })
                setHasChanges(true)
              }}
              className="input-field"
              placeholder="info@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">住所</label>
            <input
              type="text"
              value={orgSettings.address}
              onChange={(e) => {
                setOrgSettings({ ...orgSettings, address: e.target.value })
                setHasChanges(true)
              }}
              className="input-field"
              placeholder="東京都渋谷区..."
            />
          </div>

          {/* Twilio設定セクション */}
          <div className="pt-5 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Twilio認証情報</h3>
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Twilio Account SID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={twilioSettings.accountSid}
                    onChange={(e) => {
                      setTwilioSettings({ ...twilioSettings, accountSid: e.target.value })
                      setHasChanges(true)
                    }}
                    className="input-field font-mono text-sm"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    autoComplete="off"
                    data-form-type="other"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Twilio Auth Token <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={twilioSettings.authToken}
                    onChange={(e) => {
                      setTwilioSettings({ ...twilioSettings, authToken: e.target.value })
                      setHasChanges(true)
                    }}
                    className="input-field font-mono text-sm"
                    placeholder="••••••••••••••••••••••••••••••••"
                    autoComplete="new-password"
                    data-form-type="other"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  本番環境推奨設定（オプション）
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Twilio API Key
                    </label>
                    <input
                      type="text"
                      value={twilioSettings.apiKey}
                      onChange={(e) => {
                        setTwilioSettings({ ...twilioSettings, apiKey: e.target.value })
                        setHasChanges(true)
                      }}
                      className="input-field font-mono text-sm"
                      placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      autoComplete="off"
                      data-form-type="other"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Twilio API Secret
                    </label>
                    <input
                      type="password"
                      value={twilioSettings.apiSecret}
                      onChange={(e) => {
                        setTwilioSettings({ ...twilioSettings, apiSecret: e.target.value })
                        setHasChanges(true)
                      }}
                      className="input-field font-mono text-sm"
                      placeholder="••••••••••••••••••••••••••••••••"
                      autoComplete="new-password"
                      data-form-type="other"
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    TwiML App SID
                  </label>
                  <input
                    type="text"
                    value={twilioSettings.twimlAppSid}
                    onChange={(e) => {
                      setTwilioSettings({ ...twilioSettings, twimlAppSid: e.target.value })
                      setHasChanges(true)
                    }}
                    className="input-field font-mono text-sm"
                    placeholder="APxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    autoComplete="off"
                    data-form-type="other"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ブラウザ内通話機能用のTwiML Appアプリケーション
                  </p>
                </div>
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    <strong>本番環境では</strong>、セキュリティのためAPI Key/Secretの使用を推奨します。
                    設定しない場合はAccount SID/Auth Tokenが使用されます。
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              組織全体で共通のTwilioアカウントを使用します。複数の電話番号を管理できます。
            </p>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleSaveOrg}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              disabled={!hasChanges || loading}
            >
              <Save className="w-4 h-4 inline mr-2" />
              保存
            </button>
          </div>
        </div>
      </div>
      )}

      {/* 電話番号タブ */}
      {activeTab === 'phones' && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Twilio電話番号</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">複数の電話番号を管理できます</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddPhoneForm(!showAddPhoneForm)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              電話番号を追加
            </button>
          </div>
        </div>

        {/* 追加フォーム */}
        {showAddPhoneForm && (
          <div className="p-6 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">新しい電話番号を追加</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={newPhone.phoneNumber}
                    onChange={(e) => {
                      const formatted = formatPhoneNumberAsYouType(e.target.value)
                      setNewPhone({ ...newPhone, phoneNumber: formatted })
                    }}
                    className="input-field"
                    placeholder="050-1234-5678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newPhone.name}
                    onChange={(e) => setNewPhone({ ...newPhone, name: e.target.value })}
                    className="input-field"
                    placeholder="例: メイン受付"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowAddPhoneForm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleAddPhone}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg"
                  disabled={loading}
                >
                  追加
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 電話番号リスト */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {phoneNumbers.length === 0 ? (
            <div className="p-12 text-center">
              <Phone className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">登録されている電話番号がありません</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">「電話番号を追加」ボタンから追加してください</p>
            </div>
          ) : (
            phoneNumbers.map((phone) => (
            <div key={phone.phoneNumberId} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{phone.name}</h3>
                    {!phone.isActive && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-semibold rounded">
                        無効
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-gray-600 dark:text-gray-400 font-mono">
                      {formatPhoneNumberForDisplay(phone.phoneNumber)}
                    </p>
                    {businessStatuses[phone.phoneNumberId] && (
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          businessStatuses[phone.phoneNumberId].isOpen
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {businessStatuses[phone.phoneNumberId].isOpen ? '営業時間中' : '営業時間外'}
                      </span>
                    )}
                  </div>
                  {phone.twilioAccountSid && (
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      Account SID: {phone.twilioAccountSid}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedPhoneForBusinessHours(phone)}
                    className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 flex items-center gap-2"
                    title="営業時間設定"
                  >
                    <Clock className="w-4 h-4" />
                    営業時間
                  </button>
                  <button
                    onClick={() => setSelectedPhoneForTaskRouting(phone)}
                    className="px-3 py-2 text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 flex items-center gap-2"
                    title="ルーティング設定"
                  >
                    <UserSquare className="w-4 h-4" />
                    ルーティング
                  </button>
                  <button
                    onClick={() => setSelectedPhoneForMobileRouting(phone)}
                    className="px-3 py-2 text-sm text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-900/30 flex items-center gap-2"
                    title="モバイル着信設定"
                  >
                    <Smartphone className="w-4 h-4" />
                    モバイル
                  </button>
                  <button
                    onClick={() => setSelectedPhoneForOptions(phone)}
                    className="px-3 py-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 flex items-center gap-2"
                    title="タグ・通話種別設定"
                  >
                    <Tag className="w-4 h-4" />
                    タグ・通話種別
                  </button>
                  <button
                    onClick={() => setSelectedPhoneForWelcomeMessage(phone)}
                    className="px-3 py-2 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 flex items-center gap-2"
                    title="着信メッセージ設定"
                  >
                    <Volume2 className="w-4 h-4" />
                    着信メッセージ
                  </button>
                  <button
                    onClick={() => setSelectedPhoneForIvr(phone)}
                    className="px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 flex items-center gap-2"
                    title="IVR設定"
                  >
                    <ListTree className="w-4 h-4" />
                    IVR
                  </button>
                  <button
                    onClick={() => handleDeletePhone(phone.phoneNumberId)}
                    className="px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
          )}
        </div>
      </div>
      )}

      {/* IVRメニュータブ */}
      {isAdmin && activeTab === 'ivr' && (
        <IvrMenusSettings />
      )}

      {/* Web通話リンクタブ */}
      {isAdmin && activeTab === 'web-call-links' && (
        <WebCallLinksSettings phoneNumbers={phoneNumbers} />
      )}

      {/* LINEタブ */}
      {isAdmin && activeTab === 'line' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">LINE Messaging API 設定</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              LINE公式アカウントのMessaging API設定を入力してください。Webhook URLは以下を設定してください。
            </p>
            {lineSettings.webhookToken ? (
              <div className="mt-3 flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <code className="text-sm text-gray-700 dark:text-gray-300 flex-1 break-all">
                  {`${import.meta.env.VITE_API_URL || 'https://api.telephone.dance'}/line/webhook/${lineSettings.webhookToken}`}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${import.meta.env.VITE_API_URL || 'https://api.telephone.dance'}/line/webhook/${lineSettings.webhookToken}`
                    )
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="コピー"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  LINE設定を保存するとWebhook URLが生成されます
                </p>
              </div>
            )}
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                チャンネルID
              </label>
              <input
                type="text"
                value={lineSettings.channelId}
                onChange={(e) => {
                  setLineSettings({ ...lineSettings, channelId: e.target.value })
                  setHasChanges(true)
                }}
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                placeholder="1234567890"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                チャンネルアクセストークン（長期）
              </label>
              <input
                type="password"
                value={lineSettings.channelAccessToken}
                onChange={(e) => {
                  setLineSettings({ ...lineSettings, channelAccessToken: e.target.value })
                  setHasChanges(true)
                }}
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                placeholder="設定済みの場合は空欄のままにしてください"
              />
              <p className="text-xs text-gray-400 mt-1">LINE Developersコンソールで発行できます</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                チャンネルシークレット
              </label>
              <input
                type="password"
                value={lineSettings.channelSecret}
                onChange={(e) => {
                  setLineSettings({ ...lineSettings, channelSecret: e.target.value })
                  setHasChanges(true)
                }}
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                placeholder="設定済みの場合は空欄のままにしてください"
              />
              <p className="text-xs text-gray-400 mt-1">Webhook署名検証に使用されます</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                友だち追加URL
              </label>
              <input
                type="url"
                value={lineSettings.friendUrl}
                onChange={(e) => {
                  setLineSettings({ ...lineSettings, friendUrl: e.target.value })
                  setHasChanges(true)
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                placeholder="https://line.me/R/ti/p/@example"
              />
              <p className="text-xs text-gray-400 mt-1">LINE公式アカウントの友だち追加リンク（QRコード生成等に使用）</p>
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveOrg}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
              >
                <Save className="w-4 h-4" />
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LINE応対タブ（自動応答・テンプレート・一括送信・統計・AI返信） */}
      {activeTab === 'line-ops' && (
        <LineSettingsPage />
      )}

      {/* 着信拒否タブ */}
      {activeTab === 'blocklist' && (
        <BlockListPage />
      )}

      {/* 監査タブ (受話可能者ゼロ期間) */}
      {isAdmin && activeTab === 'audit' && (
        <CoverageAuditView />
      )}

      {/* その他タブ */}
      {isAdmin && activeTab === 'other' && (
        <>
          {/* Slack通知設定 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Slack通知設定</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">営業時間外の着信やボイスメール録音時に通知</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Slack Webhook URL
              </label>
              <input
                type="url"
                value={orgSettings.slackWebhookUrl}
                onChange={(e) => {
                  setOrgSettings({ ...orgSettings, slackWebhookUrl: e.target.value })
                  setHasChanges(true)
                }}
                className="input-field font-mono text-sm"
                placeholder="https://hooks.slack.com/services/YOUR/WEBHOOK/URL"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                営業時間外の着信やボイスメール録音時にSlackに通知されます。
                <a
                  href="https://api.slack.com/messaging/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline ml-1"
                >
                  Webhook URLの取得方法はこちら
                </a>
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSaveOrg}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  disabled={!hasChanges || loading}
                >
                  <Save className="w-4 h-4" />
                  保存
                </button>
              </div>
            </div>
          </div>

          {/* 顧客タグ設定 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <Tag className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">顧客タグ設定</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">顧客詳細画面で使用するタグの選択肢</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                デフォルトタグ選択肢
              </label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    id="customerTagInput"
                    type="text"
                    placeholder="新しいタグを入力"
                    className="input-field flex-1"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const newTag = e.target.value.trim()
                        if (!orgSettings.customerTags.includes(newTag)) {
                          setOrgSettings({
                            ...orgSettings,
                            customerTags: [...orgSettings.customerTags, newTag]
                          })
                          setHasChanges(true)
                          e.target.value = ''
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('customerTagInput')
                      const newTag = input.value.trim()
                      if (newTag && !orgSettings.customerTags.includes(newTag)) {
                        setOrgSettings({
                          ...orgSettings,
                          customerTags: [...orgSettings.customerTags, newTag]
                        })
                        setHasChanges(true)
                        input.value = ''
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {orgSettings.customerTags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {orgSettings.customerTags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm flex items-center gap-2"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => {
                            setOrgSettings({
                              ...orgSettings,
                              customerTags: orgSettings.customerTags.filter((_, i) => i !== index)
                            })
                            setHasChanges(true)
                          }}
                          className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                顧客詳細画面でタグを追加する際の選択肢として表示されます。
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSaveOrg}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  disabled={!hasChanges || loading}
                >
                  <Save className="w-4 h-4" />
                  保存
                </button>
              </div>
            </div>
          </div>

          {/* Webhook URL */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                  <ExternalLink className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Webhook URL</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Twilio電話番号の設定に登録</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Webhook URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={webhookUrl}
                  className="input-field flex-1 font-mono text-sm"
                  readOnly
                />
                <button
                  onClick={copyWebhookUrl}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-2 space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  このURLを各Twilio電話番号の設定に登録してください
                </p>
                <a
                  href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  <ExternalLink className="w-4 h-4" />
                  Twilio Consoleで設定
                </a>
              </div>
            </div>
          </div>

          {/* セキュリティ設定 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">セキュリティ</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">セキュリティとアクセス制御の設定</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">2要素認証</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">ログイン時に追加の認証を要求します</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 非管理者用：Webhook URLのみ表示 */}
      {!isAdmin && activeTab === 'phones' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Webhook URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={webhookUrl}
                className="input-field flex-1 font-mono text-sm"
                readOnly
              />
              <button
                onClick={copyWebhookUrl}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-2 space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                このURLを各Twilio電話番号の設定に登録してください
              </p>
              <a
                href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                <ExternalLink className="w-4 h-4" />
                Twilio Consoleで設定
              </a>
            </div>
          </div>
        </div>
      )}

      {/* 営業時間設定モーダル */}
      {selectedPhoneForBusinessHours && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <BusinessHoursSettings
                phoneNumber={selectedPhoneForBusinessHours}
                onClose={() => setSelectedPhoneForBusinessHours(null)}
                onSave={fetchStatuses}
              />
            </div>
          </div>
        </div>
      )}

      {/* タスクルーティング設定モーダル */}
      {selectedPhoneForTaskRouting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <TaskRoutingSettings
                phoneNumber={selectedPhoneForTaskRouting}
                onClose={() => setSelectedPhoneForTaskRouting(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* モバイル着信設定モーダル */}
      {selectedPhoneForMobileRouting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <MobileRoutingSettings
                phoneNumber={selectedPhoneForMobileRouting}
                onClose={() => setSelectedPhoneForMobileRouting(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* タグ・通話種別設定モーダル */}
      {selectedPhoneForOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <PhoneNumberOptionsSettings
                phoneNumber={selectedPhoneForOptions}
                onClose={() => setSelectedPhoneForOptions(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* 着信メッセージ設定モーダル */}
      {selectedPhoneForWelcomeMessage && (
        <WelcomeMessageSettings
          phoneNumber={selectedPhoneForWelcomeMessage}
          onClose={() => setSelectedPhoneForWelcomeMessage(null)}
          onSaved={() => {
            // 設定保存後の処理（必要に応じて）
          }}
        />
      )}

      {/* IVR設定モーダル */}
      {selectedPhoneForIvr && (
        <IvrPhoneNumberSettings
          phoneNumber={selectedPhoneForIvr}
          onClose={() => setSelectedPhoneForIvr(null)}
        />
      )}
    </div>
  )
}
